export interface TrackerEvent {
  eventAction?: string;
  eventCategory?: string;
  event?: string;
  eventLabel?: string;
  storyIndex?: number;
}

interface TrackingProperties extends DOMStringMap {
  role?: string;
  component?: string;
  trackingId?: string;
}

export class Tracker {
  // eslint-disable-next-line no-use-before-define
  static instance: Tracker;

  elements: Map<string, HTMLElement>;

  constructor() {
    this.elements = new Map();
    Tracker.instance = this;
  }

  static getInstance() {
    return Tracker.instance || new Tracker();
  }

  registerTracking(node: HTMLElement) {
    if (!node) {
      return;
    }

    node.addEventListener("click", () => {
      const parentNode: HTMLElement = Tracker.instance.getParent(node);
      const isValidForTracking = validateElementsForTracking(parentNode, node);

      // TODO: This will be removed in a follow-up PR. getParent()
      // and validateNodeForTracking() will throw errors which bubble
      // up once we integrate with all components. - BW 4/23/25
      if (!isValidForTracking) {
        console.error(
          "There was a problem creating your event. Please confirm integration.",
        );
      }

      const eventData = generateEventFromElements(parentNode, node);

      Tracker.instance.trackEvent(eventData);
    });
  }

  registerComponent(el: HTMLElement | null) {
    if (el) {
      Tracker.instance.elements.set(el.id, el);
    }
  }

  trackEvent(config?: TrackerEvent) {
    if (!window.dataLayer) {
      console.error("window.dataLayer is not defined", config);

      return;
    }

    if (!trackerEventIsValid(config)) {
      console.error(
        "Invalid parameters passed to Tracker.trackEvent. Event will not fire.",
      );

      return;
    }

    window.dataLayer.push(config);
  }

  private getParent(child: HTMLElement | null) {
    const parent = [...Tracker.instance.elements.entries()].find(([_, el]) =>
      el.contains(child),
    );

    if (!parent) {
      throw Error(`No parent found for element ${child}`);
    }

    return parent[1];
  }
}

export function trackerEventIsValid(config?: TrackerEvent): boolean {
  if (!config) return false;
  const { event, eventAction, eventLabel, eventCategory } = config;

  // TODO: This should throw an error in the future. For
  // backwards compatibility we're console.error'ing, but there
  // will be a follow-up ticket to harden this logic. - BW 4/22/25
  if (!(event && eventCategory && eventAction && eventLabel)) {
    console.error(
      "Invalid tracking config, you are missing required properties. [event, eventCategory, eventLabel and eventAction are required]",
    );

    return false;
  }

  return true;
}

export function validateElementsForTracking(
  parentNode: HTMLElement,
  childNode: HTMLElement,
): boolean {
  const childDataset = childNode.dataset as TrackingProperties;
  const parentDataset = parentNode.dataset as TrackingProperties;

  // TODO: These if statements should throw errors, but this would
  // be a breaking change so we're going to log them to the console
  // there will be a followup ticket to harden this logic once
  // Tracking has been integrated across components. - BW 4/22/25
  if (!childNode.ariaLabel) {
    console.error("Child is missing expected attribute: aria-label");

    return false;
  }

  if (!childDataset.role) {
    console.error("Child is missing expected dataset attribute: data-role");

    return false;
  }

  if (!parentDataset.component) {
    console.error(
      "Parent is missing expected dataset attribute: data-component",
    );

    return false;
  }

  if (!parentDataset.trackingId) {
    console.error(
      "Parent is missing expected dataset attribute: data-component",
    );

    return false;
  }

  return true;
}

export function generateEventFromElements(
  parentNode: HTMLElement,
  childNode: HTMLElement,
): TrackerEvent | undefined {
  const childDataset = childNode.dataset as TrackingProperties;
  const parentDataset = parentNode.dataset as TrackingProperties;

  const intercomEvent: TrackerEvent = {
    event: "intercom_click",
    eventCategory: parentDataset.trackingId,
    eventAction: `${parentDataset.component}_${childDataset.role}_click`,
    eventLabel: childNode.ariaLabel,
  };

  return trackerEventIsValid(intercomEvent) ? intercomEvent : undefined;
}
